/* eslint-disable @typescript-eslint/no-explicit-any */
interface EventsMap {
  [event: string]: any;
}

interface DefaultEvents extends EventsMap {
  [event: string]: (...args: any[]) => void;
}

/**
 * This flag is true if execution following an emit should be immediately stopped.
 * Useful for any before-* events that prevent continued processes
 */
export type TPreventExecution = boolean;

export interface Unsubscribe {
  (): void;
}

/**
 * Default per-(instance, event) listener ceiling for the growth guard in `on()` (AVS-4332, AC-3).
 *
 * **Static and generous, deliberately.** The AVS-4332 Task 6a measurement found 7 permanent
 * `userSession()` 'change' listeners (`state/analytics.ts:65`, `utils/tenant.ts:75`,
 * `state/energyLabel.ts:25`, `utils/web-component.tsx:200`, `components/TimeoutModal/index.tsx:40`,
 * `utils/ui/toastPortal.ts:84`, `utils/ui/rtc.ts:119`) plus 106 `useAppState`/`useState()` call
 * sites across 68 files — and list-rendered components multiply those per row, so legitimate steady
 * state reaches dozens. 200 sits clear of that while still catching a real leak: the AVS-4329/4331
 * `useAppState` leak added one permanent listener per mount and climbs into the hundreds within
 * minutes of normal navigation.
 *
 * A **rolling high-water mark was considered and rejected**: a mark that tracks the current maximum
 * chases a monotonically growing leak and therefore never trips. A static ceiling above legitimate
 * steady state is what actually detects unbounded growth.
 *
 * Raise it per instance with `setListenerCeiling()` for a legitimately busier emitter.
 */
export const DEFAULT_LISTENER_CEILING = 200;

/**
 * Sink for guard trips. `event` is the event name, `listenerCount` the post-registration count.
 */
export type TListenerGuardReporter = (event: string, listenerCount: number) => void;

function noopListenerGuardReporter(): void {
  // Default sink: the guard still logs loudly, it just has no counter to feed.
}

let listenerGuardReporter: TListenerGuardReporter = noopListenerGuardReporter;

/**
 * Register a sink for listener-guard trips — AVS-4332's browser probe wires
 * `state/session/syncTelemetry`'s `recordListenerGuardTrip` here. Pass `null` to restore the no-op.
 *
 * This indirection exists because **this module must keep ZERO imports**: `utils/ui/rtc.test.ts`
 * hand-mocks `utils/log` with only `{ log, info }`, and `emit`'s try/catch isolates *listeners*, not
 * `on()` — so a `logger.error` (or a telemetry import, which would also risk a cycle) from inside
 * `on()` would throw `is not a function` in those suites. The guard therefore uses bare
 * `console.error`, matching the isolation log in `emit()`.
 */
export function setListenerGuardReporter(reporter: TListenerGuardReporter | null): void {
  listenerGuardReporter = reporter ?? noopListenerGuardReporter;
}

/**
 * Sink for the Session Flight Recorder (AVS-4426).
 *
 * `source` is the emitting instance (a `State`, a `TenantConfig`, an `RTCClient`), so a lane can
 * tell which emitter an event came from without this module importing any of them. `args` and
 * `results` are the LIVE arrays `emit()` is about to return — a lane must copy what it needs, and
 * the recorder core does exactly that, synchronously.
 *
 * **`args` has three different arities on `change` alone** and a naive destructure silently reads
 * `undefined` (that shape already shipped one production bug, AVS-4329):
 *
 * | source | args |
 * |---|---|
 * | `State.set()` (`utils/state.ts:70`), `TenantConfig.setConfig()` (`utils/tenant.ts:107`) | `[keys, values, oldValues]` |
 * | `State.setValues()` (`utils/state.ts:169`) | `[keys, values, oldValues, payload, currentState]` |
 * | the reset path (`utils/state.ts:227`) | the same plus a trailing `{ reset: true }` |
 *
 * The whole array is handed over precisely so no destructure happens here.
 *
 * ## `args` and `results` are LIVE — the sink must treat both as read-only
 *
 * They are the emitter's own arrays, handed over without a copy: `emit` returns `args` inside its
 * `[preventExecution, results, callbacks, args]` tuple, and `results` is the array the listener
 * loop filled. The core never mutates either, and no sink may: a `results.push(...)` or an
 * `args.splice(...)` from a PR-3 lane would change what the emitter itself sees, which for
 * `utils/ui/media.ts:144` and `utils/runtime.ts:125` means changing whether `getUserMedia` runs or
 * a host dialog is suppressed. Copy before you keep — the recorder's redactor already does, since
 * `utils/state.ts:232-240` mutates `$data` in place and a retained reference would read as the
 * final state.
 */
export type TEmitRecorder = (
  source: object,
  event: string,
  args: readonly unknown[],
  results: readonly unknown[]
) => void;

function noopEmitRecorder(): void {
  // Default sink: no recorder installed, and emit() pays one no-op call.
}

let emitRecorder: TEmitRecorder = noopEmitRecorder;

/**
 * Register a sink for every emission. Pass `null` to restore the no-op.
 *
 * Same discipline as `setListenerGuardReporter` above, and for the same reasons: this module keeps
 * ZERO imports, the sink is called from INSIDE the class rather than by wrapping `emit()` from
 * outside, and it is wrapped in its own try/catch so a throwing sink cannot fail an emission.
 *
 * A sink, deliberately **not** a listener. `utils/ui/media.ts:144` aborts `getUserMedia` when a
 * listener returns `false` and `utils/runtime.ts:125` suppresses host toasts and dialogs the same
 * way, so a recorder written as `on('before-initialized', () => enabled && push(e))` would disable
 * the camera it exists to observe. The sink's return value is discarded and it is invoked AFTER the
 * listener loop, so `emit`'s `[preventExecution, results, callbacks, args]` contract is untouched.
 *
 * `utils/events.ts` is a shared client + lambda + vitest graph (`LambdaHandler extends EventEmitter`,
 * `utils/lambda/LambdaHandler.ts:114`). Nothing installs this by default; the recorder installs it
 * only from `telemetry/recorder`, which is absent from every non-dev/prelive artefact, and
 * `utils/test/resetAppState` restores the no-op for suites that adopt it.
 */
export function setEmitRecorder(recorder: TEmitRecorder | null): void {
  emitRecorder = recorder ?? noopEmitRecorder;
}

/**
 * Per-(instance, event) trip bookkeeping: emitter → event name → the count we last warned at.
 *
 * A `WeakMap` (not a module-level `Map`) so the guard never retains a `State`/`RTCClient` that the
 * app has dropped. It is touched **only** on the over-ceiling branch of `on()`; the hot path stays a
 * single integer compare.
 */
const listenerGuardWarnings = new WeakMap<object, Map<string, number>>();

/**
 * Create event emitter.
 *
 * ```js
 * import { EventEmitter } from 'utils/events'
 *
 * class Ticker {
 *   constructor() {
 *     this.emitter = new EventEmitter()
 *   }
 *   on(...args) {
 *     return this.emitter.on(...args)
 *   }
 *   tick() {
 *     this.emitter.emit('tick')
 *   }
 * }
 * ```
 */
export class EventEmitter<Events extends EventsMap = DefaultEvents> {
  /**
   * Event names in keys and arrays with listeners in values.
   *
   * ```js
   * emitter1.events = emitter2.events
   * emitter2.events = { }
   * ```
   */
  events: Partial<{ [E in keyof Events]: Events[E][] }> = {};

  /**
   * Ceiling for the listener-growth guard, per event name. Subclasses may raise it directly in
   * their constructor (before any registration); anything later must go through
   * `setListenerCeiling()`, which also re-arms the guard for the new ceiling.
   */
  protected $listenerCeiling: number = DEFAULT_LISTENER_CEILING;

  /**
   * Number of listeners currently registered for an event. Exposed so callers (the sync-telemetry
   * probe) can read a count without reaching into `events` through an ad-hoc structural type, and
   * so the internal storage shape stays free to change.
   */
  listenerCount<K extends keyof Events>(this: this, event: K): number {
    return this.events[event]?.length ?? 0;
  }

  /**
   * Raise (or lower) this instance's per-event listener ceiling — for an emitter that legitimately
   * carries more listeners than `DEFAULT_LISTENER_CEILING`.
   *
   * No emitter sets a ceiling today; every one runs at the default. `SignalerClient`
   * (`webrtc/signal/index.ts`) is NOT tunable here at all — it does not extend `EventEmitter`, it
   * keeps its own `listeners` array and its own `on`/`off`, so signaler listener growth is
   * deliberately unguarded.
   */
  setListenerCeiling(this: this, ceiling: number): void {
    this.$listenerCeiling = ceiling;
    // Re-arm every event on this instance so the guard can trip once against the new ceiling.
    listenerGuardWarnings.delete(this);
  }

  /**
   * Warn once per (instance, event) that the listener count crossed the ceiling. Never throws: a
   * throwing guard would turn a benign leak into a dev-time app crash and hard-fail CI on any
   * pre-existing leak. AC-3's "trips in CI/dev" is covered by the assertion tests instead.
   */
  private tripListenerGuard(this: this, event: string, listenerCount: number): void {
    let warnedCounts = listenerGuardWarnings.get(this);
    if (!warnedCounts) {
      warnedCounts = new Map<string, number>();
      listenerGuardWarnings.set(this, warnedCounts);
    }

    // Warn once per *crossing*, not once per extra listener. A push can only land back on or below
    // the previously warned count if the count first fell back to/below the ceiling, so
    // `listenerCount <= warnedAt` is exactly the "dropped below and crossed again" re-arm — no
    // separate re-arm bookkeeping and nothing to touch on the below-ceiling hot path.
    const warnedAt = warnedCounts.get(event);
    if (warnedAt !== undefined && listenerCount > warnedAt) {
      return;
    }

    warnedCounts.set(event, listenerCount);
    console.error(
      `[EventEmitter] listener-growth guard: "${event}" on ${this.constructor.name} reached ` +
        `${listenerCount} listeners (ceiling ${this.$listenerCeiling}). This is almost always a ` +
        `missing unsubscribe; call setListenerCeiling() if the emitter is legitimately this busy.`
    );

    // The sink is registered from outside and arbitrary — a throwing reporter must never make a
    // plain `on()` call fail. Only the call itself is inside the try.
    try {
      listenerGuardReporter(event, listenerCount);
    } catch (error) {
      console.error('[EventEmitter] listener-guard reporter threw and was isolated', error);
    }
  }

  /**
   * Add a listener for a given event.
   *
   * ```js
   * const unbind = ee.on('tick', (tickType, tickDuration) => {
   *   count += 1
   * })
   *
   * disable () {
   *   unbind()
   * }
   * ```
   *
   * @param event The event name.
   * @param cb The listener function.
   * @returns Unbind listener from event.
   */
  on<K extends keyof Events>(this: this, event: K, cb: Events[K]): Unsubscribe {
    this.events[event] = this.events[event] || [];

    this.events[event]?.push(cb);

    // Listener-growth guard (AVS-4332): `on()` is the single registration point for the whole app,
    // so an unbounded `on()`-without-unsubscribe leak is only visible here. The hot path is this one
    // integer compare — the WeakMap bookkeeping is touched exclusively inside `tripListenerGuard`.
    // `once()` registers through here too, so its registrations count toward the ceiling.
    const listenerCount = this.events[event]?.length ?? 0;
    if (listenerCount > this.$listenerCeiling) {
      this.tripListenerGuard(String(event), listenerCount);
    }

    return () => (this.events[event] = (this.events[event] || [])?.filter((callback) => callback !== cb));
  }

  /**
   * Calls each of the listeners registered for a given event.
   *
   * ```js
   * ee.emit('tick', tickType, tickDuration)
   * ```
   *
   * @param event The event name.
   * @param args The arguments for listeners.
   */
  emit<K extends keyof Events>(
    this: this,
    event: K,
    ...args: Parameters<Events[K]>
  ): [TPreventExecution, unknown[], Events[K][] | undefined, Parameters<Events[K]>] {
    const callbacks = this.events[event];
    let callbackResults = [];
    if (Array.isArray(callbacks)) {
      // Isolate each listener: a throwing listener must not abort the remaining
      // listeners or propagate into the emitting caller (e.g. a bad React-bridge
      // listener must never break State.setValues or the sync layer). The error is
      // logged loudly and the listener's slot is filled with `undefined`. A throw is
      // deliberately NOT treated as `false`, so it never trips the prevent-execution
      // protocol below. Delivery stays synchronous and in registration order.
      callbackResults = callbacks.map((callback) => {
        try {
          return callback(...args);
        } catch (error) {
          console.error(`[EventEmitter] listener for "${String(event)}" threw and was isolated`, error);
          return undefined;
        }
      });
    }
    const anyCallbackReturnedFalse = callbackResults.find((res) => res === false) === false;

    // AVS-4426 recorder sink. Runs AFTER the listener loop and its return value is discarded, so it
    // can neither reorder delivery nor reach the prevent-execution protocol. Isolated in its own
    // try/catch: a throwing recorder must never make an emission fail.
    try {
      emitRecorder(this, String(event), args, callbackResults);
    } catch (error) {
      console.error(`[EventEmitter] emit recorder for "${String(event)}" threw and was isolated`, error);
    }

    return [anyCallbackReturnedFalse, callbackResults, callbacks, args];
  }

  /**
   * Add a one-time listener for a given event.
   *
   * ```js
   * ee.once('tick', (tickType, tickDuration) => {
   *   count += 1
   * })
   * ```
   *
   * Registers through `on()`, so a `once` listener counts toward the listener-growth ceiling while
   * it is still pending — an emitter accumulating never-fired `once` listeners is a real leak too.
   *
   * @param event The event name.
   * @param cb The listener function.
   * @returns Unbind listener from event.
   */
  once<K extends keyof Events>(this: this, event: K, cb: Events[K]): Unsubscribe {
    const unbind = this.on(event, ((...args: Parameters<Events[K]>) => {
      // Unbind in `finally` so a throwing listener is still removed — with emit's per-listener
      // error isolation the throw no longer aborts the emit, so without this a throwing `once`
      // listener would stay registered and re-fire. The wrapper intentionally returns undefined.
      try {
        cb(...args);
      } finally {
        unbind();
      }
    }) as Events[K]);
    return unbind;
  }

  destroy(): void {
    // remove all events
    this.events = {};
  }
}
